import type { ErrorType } from '@cocoonspace/sdk-js/axios-client/use-custom-instance'
import {
	type listSpaces,
	useListSpaces,
} from '@cocoonspace/sdk-js/domains/spaces/spaces'
import type { InvalidResponse } from '@cocoonspace/sdk-js/types/invalidResponse'
import type { ListSpacesParams } from '@cocoonspace/sdk-js/types/listSpacesParams'
import type { UnauthorizedResponse } from '@cocoonspace/sdk-js/types/unauthorizedResponse'
import type { UnexpectedResponse } from '@cocoonspace/sdk-js/types/unexpectedResponse'
import type { Zone } from '@cocoonspace/sdk-js/types/zone'
import { dateTimeUtils } from '@cocoonspace/shared/utils/date-time-utils'
import { getCleanObject } from '@cocoonspace/shared/utils/get-clean-object'
import type { UseQueryResult } from '@tanstack/react-query'
import { usePlausible } from 'next-plausible'
import { useRouter } from 'next/router'
import {
	type Dispatch,
	type PropsWithChildren,
	type SetStateAction,
	createContext,
	useContext,
	useEffect,
	useState,
} from 'react'
import { select } from '~/domains/search/config/select-api-keys'
import { useSearchFilters } from '../components/filters/use-search-filters.hook'

interface UserProviderProps {
	spaceType?: 'cocoon' | 'desk'
}

export interface SearchContextType extends ReturnType<typeof useSearchFilters> {
	activeAlternative?: string
	setActiveAlternative: Dispatch<SetStateAction<string | undefined>>
	spacesListQuery: UseQueryResult<
		Awaited<ReturnType<typeof listSpaces>>,
		ErrorType<InvalidResponse | UnauthorizedResponse | UnexpectedResponse>
	>
	hasAlternatives: boolean
	hasNoResults: boolean
	spaceType: 'cocoon' | 'desk'
}

export const SearchContext = createContext<SearchContextType>(
	{} as SearchContextType,
)

export const useSearchSpaces = () => useContext(SearchContext)

export const SearchProvider = ({
	spaceType = 'cocoon',
	children,
}: PropsWithChildren<UserProviderProps>) => {
	const plausible = usePlausible()
	const router = useRouter()
	const queryState = useSearchFilters()

	const spacesListQuery = useListSpaces(
		{
			select,
			zone: router.query.zone as Zone,
			...getCleanObject(queryState.filters || ({} as ListSpacesParams)),
			...(router.route === '/desks/[zone]' && { offer: ['desk'] }),
		} as ListSpacesParams,
		{
			query: {
				keepPreviousData: true,
				select: (res) => {
					if (spaceType === 'cocoon') return res
					if (!queryState.filters.day) return res

					const spaces = res.data?.filter((space) => {
						const today = dateTimeUtils()

						const date = queryState.filters.day?.[0] as string

						if (space.desk_type === 'cocoon') {
							return dateTimeUtils(date).isBefore(today.add(7, 'day'))
						} else {
							return dateTimeUtils(date).isAfter(today.add(3, 'day'))
						}
					})

					return {
						data: spaces,
						meta: res.meta,
					}
				},
			},
		},
	)

	useEffect(() => {
		if (spacesListQuery.isFetched) {
			const filters = getCleanObject(queryState.filters)
			const isMapVisible = queryState.isMapVisible
			const zone = router.query.zone as string
			const results = spacesListQuery.data?.data.length || 0
			const alternatives = spacesListQuery.data?.meta?.alternatives?.length || 0

			const props = {
				zone,
				...filters,
				isMapVisible,
				results,
				alternatives,
			}

			plausible('search_results', { props })

			// track('search_results', props)
		}
	}, [spacesListQuery.isFetched])

	const [activeAlternative, setActiveAlternative] = useState<
		string | undefined
	>()

	const hasAlternatives = !!spacesListQuery.data?.meta?.alternatives?.length

	const hasNoResults =
		!spacesListQuery.isLoading &&
		!spacesListQuery.data?.data.length &&
		!hasAlternatives

	return (
		<SearchContext.Provider
			value={{
				...queryState,
				spaceType,
				spacesListQuery,
				activeAlternative,
				hasAlternatives,
				hasNoResults,
				setActiveAlternative,
			}}
		>
			{children}
		</SearchContext.Provider>
	)
}
